import cookiesStorage from '@/utils/cookiesStorage'
import appConfig from '@/configs/app.config'
import {
    TOKEN_NAME_IN_STORAGE,
    REFRESH_TOKEN_NAME_IN_STORAGE,
} from '@/constants/api.constant'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { mutate as swrMutate } from 'swr'
import { resetBillingUi } from './billingStore'
import { resetPlanActivation } from './planActivationStore'
import { firebaseSignOut } from '@/auth/firebase'
import type { User } from '@/@types/auth'

type Session = {
    signedIn: boolean
}

type AuthState = {
    session: Session
    user: User
}

type AuthAction = {
    setSessionSignedIn: (payload: boolean) => void
    setUser: (payload: User) => void
}

const getPersistStorage = () => {
    if (appConfig.accessTokenPersistStrategy === 'localStorage') {
        return localStorage
    }

    if (appConfig.accessTokenPersistStrategy === 'sessionStorage') {
        return sessionStorage
    }

    return cookiesStorage
}

const initialState: AuthState = {
    session: {
        signedIn: false,
    },
    user: {
        userId: '',
        avatar: '',
        userName: '',
        email: '',
        authority: [],
        // Null rather than absent, so a persisted session from before the
        // organisation layer reads as "not known yet" instead of as a company
        // account. `/auth/me` fills it on the next load.
        organization: null,
        role: null,
    },
}

export const useSessionUser = create<AuthState & AuthAction>()(
    persist(
        (set) => ({
            ...initialState,
            setSessionSignedIn: (payload) =>
                set((state) => ({
                    session: {
                        ...state.session,
                        signedIn: payload,
                    },
                })),
            setUser: (payload) =>
                set((state) => ({
                    user: {
                        ...state.user,
                        ...payload,
                    },
                })),
        }),
        { name: 'sessionUser', storage: createJSONStorage(() => localStorage) },
    ),
)

/**
 * Token access as plain functions, not a hook.
 *
 * The axios interceptors need to read and rewrite tokens from outside React —
 * during a request, with no component rendering — so the storage layer cannot
 * live behind hook rules. `useToken` below is a thin wrapper that keeps the
 * template's call signature for component code.
 */
export const tokenStorage = {
    get access() {
        return getPersistStorage().getItem(TOKEN_NAME_IN_STORAGE) || ''
    },
    get refresh() {
        return getPersistStorage().getItem(REFRESH_TOKEN_NAME_IN_STORAGE) || ''
    },
    set(accessToken: string, refreshToken?: string) {
        const storage = getPersistStorage()
        storage.setItem(TOKEN_NAME_IN_STORAGE, accessToken)
        if (refreshToken !== undefined) {
            storage.setItem(REFRESH_TOKEN_NAME_IN_STORAGE, refreshToken)
        }
    },
    clear() {
        const storage = getPersistStorage()
        storage.removeItem(TOKEN_NAME_IN_STORAGE)
        storage.removeItem(REFRESH_TOKEN_NAME_IN_STORAGE)
    },
}

/**
 * Wipe every trace of the session.
 *
 * Called from sign-out and from the response interceptor when a refresh fails.
 *
 * Clearing the SWR cache is not optional here, and it is the one thing the
 * template did not need to do. SWR caches responses in memory keyed by URL,
 * and that cache outlives a sign-out — so the next person to sign in on this
 * browser would be served the previous account's meetings and client list from
 * cache while their own requests are still in flight. That is a cross-account
 * bleed rendered on screen, which CLAUDE.md §2.1 treats as launch-blocking.
 * Server-side scoping is still the real guard; this closes the client-side half.
 *
 * Ending the Firebase session is the other half of the same idea. Firebase is
 * configured with tab-scoped persistence precisely so it cannot outlive much
 * (see `auth/firebase.ts`), but "not much" is not "nothing": a session left
 * standing in this tab could be refreshed into a fresh ID token after the user
 * pressed Sign Out, on a machine they may not own.
 */
export const clearSession = () => {
    tokenStorage.clear()
    // Fire and forget, never awaited. Same reasoning as `AuthProvider.signOut`'s
    // `finally`: the user asked to be signed out, and a network failure talking
    // to Firebase must not leave our own teardown half-done.
    void firebaseSignOut().catch(() => {})
    useSessionUser.getState().setUser(initialState.user)
    useSessionUser.getState().setSessionSignedIn(false)
    // Matcher form: drop every cached key, and do not revalidate them.
    swrMutate(() => true, undefined, false)
    // A quota dialog left standing would show the previous account's plan
    // limits to whoever signs in next — the store outlives the route tree.
    resetBillingUi()
    // Likewise "Activating Pro…" in the next person's drawer.
    resetPlanActivation()
}

export const useToken = () => {
    const setToken = (token: string, refreshToken?: string) => {
        tokenStorage.set(token, refreshToken)
    }

    return {
        setToken,
        token: tokenStorage.access,
        refreshToken: tokenStorage.refresh,
    }
}
